// ─────────────────────────────────────────────────────────────────────────────
// Grouping for the two right-hand panels: Review Mode's "Review Summary" and
// Live Review's "Meeting Agenda".
//
// Both used to render a flat list built by concatenating three arrays -
// blocks (sorted by reviewed_at), then section comments, then the overall
// comment - which meant a section comment always landed at the very bottom,
// far from the blocks it was about, and blocks were ordered by whenever the
// coach happened to click rather than by anything meaningful afterwards.
//
// Here both panels are built from the canvas's own band > section order (see
// StrategyCanvasStructure.js), so a section comment sits at the head of its
// own section and the right-hand panel reads in the same order as the canvas
// on the left. The canvas-wide comment becomes its own trailing group rather
// than a card outside the list.
// ─────────────────────────────────────────────────────────────────────────────

import { buildBands, getBlockLabel } from "./StrategyCanvasStructure";

export const ITEM_KIND = {
  BLOCK: "block",
  SECTION: "section",
  OVERALL: "overall",
};

// The trailing "whole canvas" group. No band of its own - it deliberately
// renders without the band eyebrow the canvas sections carry.
const OVERALL_GROUP_KEY = "__overall__";
const OVERALL_GROUP_NAME = "Whole canvas";

const hasText = (comment) =>
  !!(comment?.comment_text && comment.comment_text.trim());

// Section-scoped remarks come back keyed by the layer's own name rather than
// by a block key, and the exact spelling has varied ("Customers & Promise" vs
// "customers_promise"). Normalising both sides is what stops a section comment
// falling through to the canvas-wide bucket at the end of the list.
const normaliseKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "");

// A group is one section's worth of items, plus the labels needed to head it.
// `band` is null for the overall group so the heading can drop the eyebrow.
const makeGroup = (band, section, items) => ({
  key: section ? `${band.key}-${section.key}` : OVERALL_GROUP_KEY,
  // Which band/section this group came from. Live Review needs it to know
  // which band to unfold when the coach clicks an agenda item; null on the
  // canvas-wide group, which belongs to no band.
  bandKey: band ? band.key : null,
  sectionKey: section ? section.key : null,
  // Sections whose band label already names them (Growth / Core / DNA) read
  // better as just the band name than as "Growth Strategy / Growth".
  band: section && !section.hideHeading ? band.label : null,
  name: section ? (section.hideHeading ? band.label : section.title) : OVERALL_GROUP_NAME,
  color: band ? band.color : "core",
  items,
});

// ─── Review Mode — "Review Summary" ──────────────────────────────────────────
// Shows what the coach decided: every block they flagged to discuss, every
// block they accepted but commented on, each section comment at the head of
// its section, and the overall comment last.
export const buildReviewSummary = (review) => {
  if (!review) return [];
  const bands = buildBands(review.layers);
  const groups = [];

  bands.forEach((band) => {
    band.sections.forEach((section) => {
      const items = [];

      // Section comment first - it frames the blocks underneath it.
      (section.layer?.section_comments || []).filter(hasText).forEach((comment) => {
        items.push({
          id: `section-${comment.coach_comment_id}`,
          kind: ITEM_KIND.SECTION,
          title: section.hideHeading ? band.label : section.title,
          status: null,
          comments: [comment],
        });
      });

      section.groups.forEach((group) => {
        group.blocks.forEach((block) => {
          const comments = (block.comments || []).filter(hasText);
          const flagged = block.review_status === "to_discuss";
          if (!flagged && !comments.length) return;
          items.push({
            id: `block-${block.block_key}`,
            kind: ITEM_KIND.BLOCK,
            title: getBlockLabel(block),
            status: block.review_status,
            comments,
          });
        });
      });

      if (items.length) groups.push(makeGroup(band, section, items));
    });
  });

  const overall = (review.overall_comments || []).filter(hasText);
  if (overall.length) {
    groups.push(
      makeGroup(null, null, [
        {
          id: `overall-${overall[0].coach_comment_id}`,
          kind: ITEM_KIND.OVERALL,
          title: "Overall assessment",
          status: null,
          comments: overall,
        },
      ]),
    );
  }

  return groups;
};

// ─── Live Review — "Meeting Agenda" ──────────────────────────────────────────
// Driven by the 7.4 `remarks` list rather than by the blocks, since a remark
// is the thing that gets marked discussed. One item per remark, so the
// mark-discussed call keeps its existing remark_type + id semantics - the
// only thing that changes is where each item sits in the list.
//
// `remarks` carry block_key/block_label but no section, so the payload's own
// layers are used to work out which layer a block belongs to.
// Sorts the flat remarks list into block-scoped, section-scoped and
// canvas-wide buckets.
//
// Exported because the Live Review canvas needs the section bucket too: unlike
// the review (5.2) payload, the live-review (7.4) payload carries NO
// section_comments on its layers - a section comment arrives as a remark like
// everything else. Reading layer.section_comments there silently renders
// nothing, which is exactly what it did.
export const groupLiveReviewRemarks = ({ layers = [], remarks = [] } = {}) => {
  const layerByBlockKey = new Map();
  const layerByName = new Map();
  (layers || []).forEach((layer) => {
    layerByName.set(normaliseKey(layer.layer), layer.layer);
    (layer.blocks || []).forEach((block) => {
      if (block.block_key) layerByBlockKey.set(block.block_key, layer.layer);
    });
  });

  // A remark that isn't tied to a block may still be tied to a section - the
  // key arrives under scope_key, block_key or block_label depending on the
  // payload, so all three are tried before giving up.
  const resolveSectionLayer = (remark) => {
    if (remark.scope_type === "overall") return null;
    const candidates = [remark.scope_key, remark.block_key, remark.block_label];
    for (const candidate of candidates) {
      const match = layerByName.get(normaliseKey(candidate));
      if (match) return match;
    }
    return null;
  };

  const blockRemarksByLayer = new Map();
  const sectionRemarksByLayer = new Map();
  const looseRemarks = [];

  const pushTo = (map, key, remark) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(remark);
  };

  (remarks || []).forEach((remark) => {
    const blockLayer = layerByBlockKey.get(remark.block_key);
    if (blockLayer) {
      pushTo(blockRemarksByLayer, blockLayer, remark);
      return;
    }
    const sectionLayer = resolveSectionLayer(remark);
    if (sectionLayer) {
      pushTo(sectionRemarksByLayer, sectionLayer, remark);
      return;
    }
    // Genuinely canvas-wide - the only thing that should reach the trailing
    // "Whole canvas" group.
    looseRemarks.push(remark);
  });

  return { blockRemarksByLayer, sectionRemarksByLayer, looseRemarks };
};

// Open agenda items per block, so a canvas row can clear its own.
//
// Keyed off real block keys only. A remark's block_key sometimes carries a
// section name rather than a block slug (which is exactly why
// resolveSectionLayer above exists) - those belong to their section, and
// matching them here would hang a "Mark as complete" button on whichever block
// happened to share the string.
export const getOpenRemarksByBlock = ({ layers = [], remarks = [] } = {}) => {
  const blockKeys = new Set(
    (layers || []).flatMap((layer) =>
      (layer.blocks || []).map((block) => block.block_key),
    ),
  );
  const byBlock = new Map();
  (remarks || []).forEach((remark) => {
    if (remark.discussed || !blockKeys.has(remark.block_key)) return;
    if (!byBlock.has(remark.block_key)) byBlock.set(remark.block_key, []);
    byBlock.get(remark.block_key).push(remark);
  });
  return byBlock;
};

export const buildAgendaGroups = (liveReview) => {
  if (!liveReview) return [];
  const layers = liveReview.layers || [];

  const { blockRemarksByLayer, sectionRemarksByLayer, looseRemarks } =
    groupLiveReviewRemarks(liveReview);

  // Only blocks carry a status. A block is in the agenda because it was
  // flagged, so "to discuss" is exactly what it is. A section or overall
  // comment is the coach's remark about a scope rather than a decision on
  // anything, so it has no status to show and its badge names it instead.
  const toItem = (remark, kind, title, sectionKey) => ({
    id: `${remark.remark_type}-${remark.id}`,
    kind,
    title: title || remark.block_label || "Canvas",
    status: kind === ITEM_KIND.BLOCK ? "to_discuss" : null,
    comments: hasText(remark) ? [{ comment_text: remark.comment_text }] : [],
    discussed: !!remark.discussed,
    remark,
    // Matches a data-agenda-target on the canvas, so clicking this item can
    // find the thing it refers to. Keyed by what the item is *about* rather
    // than by remark id, because the canvas draws blocks and sections, not
    // remarks.
    targetKey:
      kind === ITEM_KIND.BLOCK
        ? `block:${remark.block_key}`
        : kind === ITEM_KIND.SECTION
          ? `section:${sectionKey}`
          : "overall",
  });

  const bands = buildBands(layers, (block) => block.status || block.review_status);
  const groups = [];

  bands.forEach((band) => {
    band.sections.forEach((section) => {
      const sectionTitle = section.hideHeading ? band.label : section.title;
      // Section comment first - it frames the blocks underneath it, same as
      // the Review Summary.
      const items = [
        ...(sectionRemarksByLayer.get(section.key) || []).map((remark) =>
          toItem(remark, ITEM_KIND.SECTION, sectionTitle, section.key),
        ),
        ...(blockRemarksByLayer.get(section.key) || []).map((remark) =>
          toItem(remark, ITEM_KIND.BLOCK, null, section.key),
        ),
      ];
      if (items.length) groups.push(makeGroup(band, section, items));
    });
  });

  // Only canvas-wide remarks reach here; section-scoped ones were routed to
  // their own section above.
  if (looseRemarks.length) {
    groups.push(
      makeGroup(
        null,
        null,
        looseRemarks.map((remark) => toItem(remark, ITEM_KIND.OVERALL)),
      ),
    );
  }

  return groups;
};

// ─── Shared helpers ──────────────────────────────────────────────────────────
export const countItems = (groups) =>
  groups.reduce((total, group) => total + group.items.length, 0);

export const countWhere = (groups, predicate) =>
  groups.reduce(
    (total, group) => total + group.items.filter(predicate).length,
    0,
  );

// Filters items inside each group and drops any group left empty, so a
// filtered view never renders a heading with nothing under it.
export const filterGroups = (groups, predicate) =>
  groups
    .map((group) => ({ ...group, items: group.items.filter(predicate) }))
    .filter((group) => group.items.length > 0);
